import React, { useState } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Alert,
  CircularProgress,
  Button,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Chip,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  Error as CriticalIcon,
  Warning as HighIcon,
  Info as MediumIcon,
  CheckCircle as LowIcon,
  Refresh as RefreshIcon,
  Build as FixIcon,
  Assessment as AssessmentIcon,
  ExpandMore as ExpandMoreIcon,
  BugReport as BugIcon,
} from '@mui/icons-material';
import { useQuery, useMutation } from '@tanstack/react-query';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/v1';

interface DataIssue {
  type: string;
  description: string;
  affected_count?: number;
  affected_records?: any[];
  recommendation: string;
}

interface DataConsistencyReport {
  critical: DataIssue[];
  high: DataIssue[];
  medium: DataIssue[];
  low: DataIssue[];
  summary: Record<string, any>;
  overall_statistics: {
    total_critical_issues: number;
    total_high_issues: number;
    total_medium_issues: number;
    total_low_issues: number;
    total_issues: number;
    data_quality_score: number;
    check_timestamp: string;
  };
}

const DataConsistencyDashboard: React.FC = () => {
  const [fixDialogOpen, setFixDialogOpen] = useState(false);
  const [selectedFixType, setSelectedFixType] = useState('');
  const [dryRun, setDryRun] = useState(true);

  const { data: reportData, isLoading, error, refetch } = useQuery<{ data: DataConsistencyReport }>({
    queryKey: ['data-consistency-check'],
    queryFn: async () => {
      const token = localStorage.getItem('emis_auth_token');
      const response = await axios.get(
        `${API_URL}/sis-integration/data-consistency-check/`,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      return response.data;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const fixDataMutation = useMutation({
    mutationFn: async (fixData: { fix_type: string; dry_run: boolean }) => {
      const token = localStorage.getItem('emis_auth_token');
      const response = await axios.post(
        `${API_URL}/sis-integration/fix-data-issues/`,
        fixData,
        {
          headers: {
            Authorization: `Token ${token}`,
          },
        }
      );
      return response.data;
    },
    onSuccess: () => {
      refetch();
      setFixDialogOpen(false);
    },
  });

  const getPriorityIcon = (priority: string) => {
    switch (priority) {
      case 'critical':
        return <CriticalIcon color="error" />;
      case 'high':
        return <HighIcon color="warning" />;
      case 'medium':
        return <MediumIcon color="info" />;
      default:
        return <LowIcon color="success" />;
    }
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'critical':
        return 'error';
      case 'high':
        return 'warning';
      case 'medium':
        return 'info';
      default:
        return 'success';
    }
  };

  const getQualityScoreColor = (score: number) => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const handleFixIssues = () => {
    if (selectedFixType && selectedFixType !== '') {
      fixDataMutation.mutate({
        fix_type: selectedFixType,
        dry_run: dryRun,
      });
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error || !reportData) {
    return (
      <Alert severity="error">
        Failed to load data consistency report. Please try again.
      </Alert>
    );
  }

  const report = reportData.data;

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4" component="h1">
          Data Consistency Dashboard
        </Typography>
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={() => refetch()}
          >
            Refresh Check
          </Button>
          <Button
            variant="contained"
            startIcon={<FixIcon />}
            color="primary"
            onClick={() => setFixDialogOpen(true)}
          >
            Fix Issues
          </Button>
        </Box>
      </Box>

      {/* Overall Statistics */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <AssessmentIcon color="primary" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {report.overall_statistics.data_quality_score}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Data Quality Score
              </Typography>
              <Box sx={{ mt: 2 }}>
                <LinearProgress
                  variant="determinate"
                  value={report.overall_statistics.data_quality_score}
                  color={getQualityScoreColor(report.overall_statistics.data_quality_score)}
                  sx={{ height: 8, borderRadius: 4 }}
                />
              </Box>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <CriticalIcon color="error" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {report.overall_statistics.total_critical_issues}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Critical Issues
              </Typography>
              <Chip
                label="Immediate Action"
                color="error"
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <HighIcon color="warning" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {report.overall_statistics.total_high_issues}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                High Priority
              </Typography>
              <Chip
                label="Urgent"
                color="warning"
                size="small"
                sx={{ mt: 1 }}
              />
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent sx={{ textAlign: 'center' }}>
              <BugIcon color="info" sx={{ fontSize: 40, mb: 1 }} />
              <Typography variant="h4">
                {report.overall_statistics.total_issues}
              </Typography>
              <Typography variant="subtitle1" color="text.secondary">
                Total Issues
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Last checked: {new Date(report.overall_statistics.check_timestamp).toLocaleString()}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Issues by Priority */}
      {['critical', 'high', 'medium', 'low'].map((priority) => {
        const issues = report[priority as keyof typeof report] as DataIssue[];
        if (issues.length === 0) return null;

        return (
          <Accordion key={priority} sx={{ mb: 2 }}>
            <AccordionSummary 
              expandIcon={<ExpandMoreIcon />}
              sx={{ 
                backgroundColor: priority === 'critical' ? 'error.light' : 
                               priority === 'high' ? 'warning.light' :
                               priority === 'medium' ? 'info.light' : 'success.light',
                color: 'white'
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                {getPriorityIcon(priority)}
                <Typography variant="h6">
                  {priority.charAt(0).toUpperCase() + priority.slice(1)} Priority Issues ({issues.length})
                </Typography>
              </Box>
            </AccordionSummary>
            <AccordionDetails>
              <List>
                {issues.map((issue, index) => (
                  <ListItem key={index} divider>
                    <ListItemIcon>
                      {getPriorityIcon(priority)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Typography variant="subtitle1">
                            {issue.type.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                          </Typography>
                          {issue.affected_count && (
                            <Chip
                              label={`${issue.affected_count.toLocaleString()} affected`}
                              size="small"
                              color={getPriorityColor(priority) as any}
                              variant="outlined"
                            />
                          )}
                        </Box>
                      }
                      secondary={
                        <Box>
                          <Typography variant="body2" sx={{ mb: 1 }}>
                            {issue.description}
                          </Typography>
                          <Typography variant="body2" color="primary" sx={{ fontWeight: 'bold' }}>
                            Recommendation: {issue.recommendation}
                          </Typography>
                          {issue.affected_records && (
                            <Box sx={{ mt: 1 }}>
                              <Typography variant="caption" color="text.secondary">
                                Sample affected records:
                              </Typography>
                              <Box component="pre" sx={{ fontSize: 12, color: 'text.secondary', mt: 0.5 }}>
                                {JSON.stringify(issue.affected_records.slice(0, 3), null, 2)}
                              </Box>
                            </Box>
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </AccordionDetails>
          </Accordion>
        );
      })}

      {/* Summary by Category */}
      <Card sx={{ mt: 3 }}>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Data Quality Summary by Category
          </Typography>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Category</TableCell>
                  <TableCell>Critical Issues</TableCell>
                  <TableCell>High Issues</TableCell>
                  <TableCell>Medium Issues</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(report.summary).map(([category, data]) => {
                  if (typeof data !== 'object') return null;
                  
                  const criticalCount = Object.values(data).filter((v: any) => 
                    typeof v === 'number' && v > 1000
                  ).length;
                  
                  const highCount = Object.values(data).filter((v: any) => 
                    typeof v === 'number' && v > 100 && v <= 1000
                  ).length;
                  
                  const mediumCount = Object.values(data).filter((v: any) => 
                    typeof v === 'number' && v > 0 && v <= 100
                  ).length;

                  return (
                    <TableRow key={category}>
                      <TableCell>
                        {category.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={criticalCount}
                          color={criticalCount > 0 ? 'error' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={highCount}
                          color={highCount > 0 ? 'warning' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={mediumCount}
                          color={mediumCount > 0 ? 'info' : 'default'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {criticalCount > 0 ? (
                          <Chip label="Critical" color="error" size="small" />
                        ) : highCount > 0 ? (
                          <Chip label="Needs Attention" color="warning" size="small" />
                        ) : mediumCount > 0 ? (
                          <Chip label="Minor Issues" color="info" size="small" />
                        ) : (
                          <Chip label="Good" color="success" size="small" />
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>

      {/* Fix Issues Dialog */}
      <Dialog open={fixDialogOpen} onClose={() => setFixDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Fix Data Issues</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Fix Type</InputLabel>
                <Select
                  value={selectedFixType}
                  label="Fix Type"
                  onChange={(e) => setSelectedFixType(e.target.value)}
                >
                  <MenuItem value="standardize_gender">Standardize Gender Values (M/F)</MenuItem>
                  <MenuItem value="remove_duplicate_students">Remove Duplicate Students</MenuItem>
                  <MenuItem value="fix_invalid_dates">Fix Invalid Birth Dates</MenuItem>
                  <MenuItem value="standardize_attendance">Standardize Attendance Values</MenuItem>
                  <MenuItem value="link_orphan_enrollments">Clean Orphan Enrollments</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Execution Mode</InputLabel>
                <Select
                  value={dryRun ? 'dry_run' : 'execute'}
                  label="Execution Mode"
                  onChange={(e) => setDryRun(e.target.value === 'dry_run')}
                >
                  <MenuItem value="dry_run">Dry Run (Preview Only)</MenuItem>
                  <MenuItem value="execute">Execute Changes</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <Alert severity={dryRun ? "info" : "warning"}>
                {dryRun 
                  ? "Dry run mode will show what changes would be made without actually modifying data."
                  : "⚠️ This will make actual changes to the database. Make sure you have a backup!"
                }
              </Alert>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFixDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={handleFixIssues} 
            variant="contained"
            disabled={!selectedFixType || fixDataMutation.isPending}
            color={dryRun ? "primary" : "warning"}
          >
            {fixDataMutation.isPending ? (
              <CircularProgress size={20} />
            ) : (
              dryRun ? "Preview Changes" : "Execute Fix"
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default DataConsistencyDashboard;